// src/components/Chat/EntityChatNotasPanel.js
import React, { useEffect, useState, useRef } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  Chip,
  Stack,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Tooltip,
  Divider,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SendIcon from "@mui/icons-material/Send";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import CloseIcon from "@mui/icons-material/Close";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import dayjs from "dayjs";
import { jwtDecode } from "jwt-decode";
import { useTranslation } from "react-i18next";

import {
  getNotasByEntityWithUser,
  addNotaToEntity,
  updateNotaEntity,
  deleteNotaEntity,
  uploadAdjuntosNota,
  getAdjuntosByNota,
  marcarNotasLeidasPorEntity,
} from "../../api/CriticalPath/criticalPathNotasApi";
import { getUsersWithAddNotesPermission } from "../../api/User/userApi";
import {
  adjuntoFileUrl,
  buildCreateNotaPayload,
  buildEditNotaUpdatePayload,
  canSaveNewNota,
  isImageAdjunto,
  shouldLoadNotas,
} from "../CriticalPathHorizontal/notasDialogUtils";

const statusColor = (status) => {
  if (status === "Closed") return "default";
  if (status === "In Progress") return "warning";
  return "success";
};

const tipoColor = (tipo) =>
  tipo === "Open Issue" ? "error" : "info";

export default function EntityChatNotasPanel({
  active = true,
  productId = null,
  idMuestra = null,
  idPresupuestoA3Pedido = null,
  onChanged,
}) {
  const { t } = useTranslation();
  const API_URL = process.env.REACT_APP_API_URL;
  const endRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [notas, setNotas] = useState([]);
  const [adjuntos, setAdjuntos] = useState({});
  const [users, setUsers] = useState([]);

  const [nota, setNota] = useState("");
  const [tipoNota, setTipoNota] = useState("Remark");
  const [writer, setWriter] = useState("");
  const [responsible, setResponsible] = useState("");
  const [status, setStatus] = useState("Open");
  const [targetDate, setTargetDate] = useState(null);
  const [closingDate, setClosingDate] = useState(null);
  const [files, setFiles] = useState([]);
  const [saving, setSaving] = useState(false);

  const [editMode, setEditMode] = useState(null);
  const [editNota, setEditNota] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [notaToDelete, setNotaToDelete] = useState(null);

  const resolveCurrentUsername = () => {
    const fromStorage = localStorage.getItem("user");
    if (fromStorage) return fromStorage;
    try {
      const token = localStorage.getItem("token");
      if (!token) return "";
      const decoded = jwtDecode(token);
      return decoded?.username || decoded?.name || "";
    } catch {
      return "";
    }
  };

  const scrollToEnd = () => {
    setTimeout(() => {
      endRef.current?.scrollIntoView({ behavior: "smooth" });
    }, 80);
  };

  const applyDefaultWriter = (userList) => {
    const currentUser = resolveCurrentUsername();
    if (!currentUser || !userList?.length) return;
    const match = userList.find(
      (u) =>
        String(u.username || "").toLowerCase() ===
        String(currentUser).toLowerCase()
    );
    if (match?.username) {
      setWriter((prev) => prev || match.username);
    }
  };

  const loadUsers = async () => {
    try {
      const data = await getUsersWithAddNotesPermission();
      const list = Array.isArray(data) ? data : [];
      setUsers((prev) => {
        const byName = new Map(
          [...list, ...(prev || [])].map((u) => [
            String(u.username || "").toLowerCase(),
            u,
          ])
        );
        const merged = [...byName.values()].sort((a, b) =>
          String(a.username || "").localeCompare(String(b.username || ""))
        );
        applyDefaultWriter(merged);
        return merged;
      });
    } catch (err) {
      console.error("Error loading note users:", err);
    }
  };

  const loadNotas = async ({ markRead = false } = {}) => {
    if (
      !shouldLoadNotas({
        open: true,
        productId,
        idMuestra,
        idPresupuestoA3Pedido,
      })
    ) {
      setNotas([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const { userId } = jwtDecode(token);
      const data = await getNotasByEntityWithUser({
        productId,
        idMuestra,
        idPresupuestoA3Pedido,
        userId,
      });
      setNotas(data || []);

      // Ensure writers/responsibles from existing notes appear in selects
      setUsers((prev) => {
        const byName = new Map(
          (prev || []).map((u) => [String(u.username || "").toLowerCase(), u])
        );
        for (const n of data || []) {
          for (const name of [n.Writer, n.Responsible]) {
            const key = String(name || "").trim().toLowerCase();
            if (!key || byName.has(key)) continue;
            byName.set(key, { id: `nota-${key}`, username: name });
          }
        }
        const merged = [...byName.values()];
        applyDefaultWriter(merged);
        return merged;
      });

      const filesMap = {};
      for (const n of data || []) {
        try {
          filesMap[n.Id] = await getAdjuntosByNota(n.Id);
        } catch {
          filesMap[n.Id] = [];
        }
      }
      setAdjuntos(filesMap);

      if (markRead) {
        await marcarNotasLeidasPorEntity({
          productId,
          idMuestra,
          idPresupuestoA3Pedido,
          userId,
        });
        onChanged?.({
          changed: true,
          productId,
          idMuestra,
          idPresupuestoA3Pedido,
          markedRead: true,
        });
      }
    } catch (err) {
      console.error("Error loading entity notes:", err);
    } finally {
      setLoading(false);
      scrollToEnd();
    }
  };

  useEffect(() => {
    if (
      !active ||
      !shouldLoadNotas({
        open: true,
        productId,
        idMuestra,
        idPresupuestoA3Pedido,
      })
    ) {
      return;
    }
    loadUsers();
    loadNotas({ markRead: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, productId, idMuestra, idPresupuestoA3Pedido]);

  useEffect(() => {
    if (status === "Closed") setClosingDate(dayjs().toDate());
    else setClosingDate(null);
  }, [status]);

  const notifyChanged = () => {
    onChanged?.({
      changed: true,
      productId,
      idMuestra,
      idPresupuestoA3Pedido,
    });
  };

  const resetForm = () => {
    setNota("");
    setFiles([]);
    setTipoNota("Remark");
    setResponsible("");
    setStatus("Open");
    setTargetDate(null);
    setClosingDate(null);
    // Keep writer as current user after send
    applyDefaultWriter(users);
  };

  const saveNota = async () => {
    if (!canSaveNewNota({ nota, writer, responsible }) || saving) return;
    setSaving(true);
    try {
      const payload = buildCreateNotaPayload({
        productId,
        idMuestra,
        idPresupuestoA3Pedido,
        nota,
        usuario: resolveCurrentUsername() || "Unknown",
        tipoNota,
        writer,
        responsible,
        status,
        targetDate,
        closingDate,
      });
      const res = await addNotaToEntity(payload);
      const notaId = res?.notaId;
      if (files.length && notaId) {
        await uploadAdjuntosNota({
          notaId,
          productId: payload.productId,
          idMuestra: payload.idMuestra,
          idPresupuestoA3Pedido: payload.idPresupuestoA3Pedido,
          files,
        });
      }
      resetForm();
      await loadNotas();
      notifyChanged();
    } catch (err) {
      console.error("Error saving note:", err);
    } finally {
      setSaving(false);
    }
  };

  const saveEdit = async (id) => {
    const updatePayload = buildEditNotaUpdatePayload(editNota);
    if (!updatePayload) return;
    try {
      await updateNotaEntity(id, updatePayload);
      setEditMode(null);
      setEditNota(null);
      await loadNotas();
      notifyChanged();
    } catch (err) {
      console.error("Error updating note:", err);
    }
  };

  const confirmDelete = async () => {
    try {
      await deleteNotaEntity(notaToDelete);
      setDeleteConfirmOpen(false);
      setNotaToDelete(null);
      await loadNotas();
      notifyChanged();
    } catch (err) {
      console.error("Error deleting note:", err);
    }
  };

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        bgcolor: "#f7f8fa",
      }}
    >
      <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", p: 1.5 }}>
        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              py: 6,
            }}
          >
            <CircularProgress size={28} />
          </Box>
        ) : notas.length === 0 ? (
          <Typography sx={{ color: "#888", mt: 2, textAlign: "center" }}>
            {t("Notas.NoNotas", { defaultValue: "No notes yet." })}
          </Typography>
        ) : (
          notas.map((n) => {
            const isEditing = editMode === n.Id;
            const bubbleBg =
              n.TipoNota === "Open Issue" ? "#FFF3E0" : "#E8F5E9";
            const borderColor =
              n.TipoNota === "Open Issue" ? "#FFB74D" : "#A5D6A7";

            return (
              <Box
                key={n.Id}
                sx={{
                  mb: 1.5,
                  p: 1.25,
                  borderRadius: 2,
                  bgcolor: bubbleBg,
                  border: `1px solid ${borderColor}`,
                  boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
                  position: "relative",
                  "&:hover .nota-actions": { opacity: 1 },
                }}
              >
                <Box
                  className="nota-actions"
                  sx={{
                    position: "absolute",
                    top: 4,
                    right: 4,
                    display: "flex",
                    opacity: { xs: 1, sm: 0 },
                    transition: "opacity 0.15s",
                    bgcolor: "rgba(255,255,255,0.85)",
                    borderRadius: 1,
                  }}
                >
                  {!isEditing && (
                    <>
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setEditMode(n.Id);
                            setEditNota({
                              nota: n.Nota,
                              tipoNota: n.TipoNota,
                              writer: n.Writer,
                              responsible: n.Responsible,
                              status: n.Status || "Open",
                              targetDate: n.TargetDate || null,
                            });
                          }}
                        >
                          <EditIcon fontSize="inherit" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => {
                            setNotaToDelete(n.Id);
                            setDeleteConfirmOpen(true);
                          }}
                        >
                          <DeleteOutlineIcon fontSize="inherit" />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                </Box>

                {isEditing ? (
                  <Stack spacing={1} sx={{ pr: 4 }}>
                    <Stack direction="row" spacing={1}>
                      <TextField
                        select
                        size="small"
                        fullWidth
                        label={t("Notes.Tipo", { defaultValue: "Type" })}
                        SelectProps={{ native: true }}
                        value={editNota.tipoNota}
                        onChange={(e) =>
                          setEditNota({ ...editNota, tipoNota: e.target.value })
                        }
                      >
                        <option value="Remark">
                          {t("Notes.remark", { defaultValue: "Remark" })}
                        </option>
                        <option value="Open Issue">
                          {t("Notes.Open issue", { defaultValue: "Open Issue" })}
                        </option>
                      </TextField>
                      <TextField
                        select
                        size="small"
                        fullWidth
                        label={t("Notes.Status", { defaultValue: "Status" })}
                        SelectProps={{ native: true }}
                        value={editNota.status}
                        onChange={(e) =>
                          setEditNota({ ...editNota, status: e.target.value })
                        }
                      >
                        <option value="Open">
                          {t("Notes.Open", { defaultValue: "Open" })}
                        </option>
                        <option value="In Progress">
                          {t("Notes.In Progress", {
                            defaultValue: "In Progress",
                          })}
                        </option>
                        <option value="Closed">
                          {t("Notes.Closed", { defaultValue: "Closed" })}
                        </option>
                      </TextField>
                    </Stack>
                    <Stack direction="row" spacing={1}>
                      <TextField
                        select
                        size="small"
                        fullWidth
                        label={t("Notes.Writer", { defaultValue: "Writer" })}
                        SelectProps={{ native: true }}
                        value={editNota.writer || ""}
                        onChange={(e) =>
                          setEditNota({ ...editNota, writer: e.target.value })
                        }
                      >
                        {users.map((u) => (
                          <option key={u.id} value={u.username}>
                            {u.username}
                          </option>
                        ))}
                      </TextField>
                      <TextField
                        select
                        size="small"
                        fullWidth
                        label={t("Notes.Responsible", {
                          defaultValue: "Responsible",
                        })}
                        SelectProps={{ native: true }}
                        value={editNota.responsible || ""}
                        onChange={(e) =>
                          setEditNota({
                            ...editNota,
                            responsible: e.target.value,
                          })
                        }
                      >
                        {users.map((u) => (
                          <option key={u.id} value={u.username}>
                            {u.username}
                          </option>
                        ))}
                      </TextField>
                    </Stack>
                    <TextField
                      multiline
                      minRows={3}
                      size="small"
                      fullWidth
                      value={editNota.nota || ""}
                      onChange={(e) =>
                        setEditNota({ ...editNota, nota: e.target.value })
                      }
                    />
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      <Button
                        size="small"
                        onClick={() => {
                          setEditMode(null);
                          setEditNota(null);
                        }}
                      >
                        Cancel
                      </Button>
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() => saveEdit(n.Id)}
                      >
                        Save
                      </Button>
                    </Stack>
                  </Stack>
                ) : (
                  <>
                    <Stack
                      direction="row"
                      spacing={0.75}
                      alignItems="center"
                      flexWrap="wrap"
                      useFlexGap
                      sx={{ mb: 0.75, pr: 5 }}
                    >
                      <Chip
                        size="small"
                        color={tipoColor(n.TipoNota)}
                        label={
                          n.TipoNota === "Open Issue"
                            ? t("Notes.Open issue", {
                                defaultValue: "Open Issue",
                              })
                            : t("Notes.remark", { defaultValue: "Remark" })
                        }
                        sx={{ fontWeight: 700, height: 22 }}
                      />
                      <Chip
                        size="small"
                        color={statusColor(n.Status)}
                        label={n.Status || "Open"}
                        variant="outlined"
                        sx={{ height: 22 }}
                      />
                      <Typography
                        variant="caption"
                        sx={{ color: "#666", ml: "auto !important" }}
                      >
                        {n.FechaNota
                          ? dayjs(n.FechaNota).format("DD/MM/YYYY HH:mm")
                          : ""}
                      </Typography>
                    </Stack>

                    <Typography
                      variant="caption"
                      sx={{
                        display: "block",
                        color: "#555",
                        fontWeight: 600,
                        mb: 0.5,
                      }}
                    >
                      {n.Writer || "—"}
                      <Box component="span" sx={{ color: "#999", mx: 0.5 }}>
                        →
                      </Box>
                      {n.Responsible || "—"}
                      {n.TargetDate ? (
                        <Box component="span" sx={{ color: "#888", ml: 1 }}>
                          · target {dayjs(n.TargetDate).format("DD/MM/YYYY")}
                        </Box>
                      ) : null}
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ whiteSpace: "pre-line", color: "#222" }}
                    >
                      {n.Nota}
                    </Typography>

                    {(adjuntos[n.Id] || []).length > 0 && (
                      <Box sx={{ mt: 1 }}>
                        {(adjuntos[n.Id] || []).map((f) => {
                          const fileUrl = adjuntoFileUrl(API_URL, f.FilePath);
                          return (
                            <Box key={f.Id || f.FileName} sx={{ mb: 0.5 }}>
                              {isImageAdjunto(f.MimeType) && (
                                <Box
                                  component="img"
                                  src={fileUrl}
                                  alt={f.FileName}
                                  sx={{
                                    maxWidth: "100%",
                                    maxHeight: 140,
                                    borderRadius: 1,
                                    display: "block",
                                    mb: 0.5,
                                  }}
                                />
                              )}
                              <Typography variant="caption">
                                <a
                                  href={fileUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{ textDecoration: "none" }}
                                >
                                  📎 {f.FileName}
                                </a>
                              </Typography>
                            </Box>
                          );
                        })}
                      </Box>
                    )}
                  </>
                )}
              </Box>
            );
          })
        )}
        <div ref={endRef} />
      </Box>

      <Divider />

      <Box
        sx={{
          p: 1.5,
          bgcolor: "#fff",
          borderTop: "1px solid #eceff1",
          boxShadow: "0 -4px 16px rgba(0,0,0,0.04)",
        }}
      >
        <Stack
          direction="row"
          spacing={0.75}
          alignItems="center"
          flexWrap="wrap"
          useFlexGap
          sx={{ mb: 1.25 }}
        >
          <Chip
            size="small"
            clickable
            label={t("Notes.remark", { defaultValue: "Remark" })}
            color={tipoNota === "Remark" ? "info" : "default"}
            variant={tipoNota === "Remark" ? "filled" : "outlined"}
            onClick={() => setTipoNota("Remark")}
            sx={{ fontWeight: 600 }}
          />
          <Chip
            size="small"
            clickable
            label={t("Notes.Open issue", { defaultValue: "Open Issue" })}
            color={tipoNota === "Open Issue" ? "error" : "default"}
            variant={tipoNota === "Open Issue" ? "filled" : "outlined"}
            onClick={() => setTipoNota("Open Issue")}
            sx={{ fontWeight: 600 }}
          />
          <Box sx={{ flex: 1, minWidth: 8 }} />
          <TextField
            select
            size="small"
            SelectProps={{ native: true }}
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            sx={{
              minWidth: 120,
              "& .MuiInputBase-root": {
                borderRadius: 2,
                bgcolor: "#f7f8fa",
                fontSize: "0.8rem",
              },
              "& fieldset": { borderColor: "#e6e8eb" },
            }}
          >
            <option value="Open">
              {t("Notes.Open", { defaultValue: "Open" })}
            </option>
            <option value="In Progress">
              {t("Notes.In Progress", { defaultValue: "In Progress" })}
            </option>
            <option value="Closed">
              {t("Notes.Closed", { defaultValue: "Closed" })}
            </option>
          </TextField>
        </Stack>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "1fr 1fr auto",
            },
            gap: 1,
            mb: 1.25,
          }}
        >
          <TextField
            select
            size="small"
            SelectProps={{ native: true }}
            value={writer}
            onChange={(e) => setWriter(e.target.value)}
            InputProps={{
              startAdornment: (
                <PersonOutlineIcon
                  sx={{ fontSize: 16, color: "#90a4ae", mr: 0.5 }}
                />
              ),
            }}
            sx={{
              "& .MuiInputBase-root": {
                borderRadius: 2,
                bgcolor: "#f7f8fa",
                fontSize: "0.8rem",
              },
              "& fieldset": { borderColor: "#e6e8eb" },
            }}
          >
            <option value="">
              {t("Notes.Writer", { defaultValue: "Writer" })}
            </option>
            {users.map((u) => (
              <option key={u.id} value={u.username}>
                {u.username}
              </option>
            ))}
          </TextField>

          <TextField
            select
            size="small"
            SelectProps={{ native: true }}
            value={responsible}
            onChange={(e) => setResponsible(e.target.value)}
            InputProps={{
              startAdornment: (
                <ArrowForwardIcon
                  sx={{ fontSize: 14, color: "#90a4ae", mr: 0.5 }}
                />
              ),
            }}
            sx={{
              "& .MuiInputBase-root": {
                borderRadius: 2,
                bgcolor: "#f7f8fa",
                fontSize: "0.8rem",
              },
              "& fieldset": { borderColor: "#e6e8eb" },
            }}
          >
            <option value="">
              {t("Notes.Responsible", { defaultValue: "Responsible" })}
            </option>
            {users.map((u) => (
              <option key={u.id} value={u.username}>
                {u.username}
              </option>
            ))}
          </TextField>

          <TextField
            type="date"
            size="small"
            InputLabelProps={{ shrink: true }}
            value={targetDate ? dayjs(targetDate).format("YYYY-MM-DD") : ""}
            onChange={(e) =>
              setTargetDate(
                e.target.value ? dayjs(e.target.value).toDate() : null
              )
            }
            InputProps={{
              startAdornment: (
                <CalendarTodayOutlinedIcon
                  sx={{ fontSize: 14, color: "#90a4ae", mr: 0.5 }}
                />
              ),
            }}
            sx={{
              minWidth: { sm: 150 },
              "& .MuiInputBase-root": {
                borderRadius: 2,
                bgcolor: "#f7f8fa",
                fontSize: "0.8rem",
              },
              "& fieldset": { borderColor: "#e6e8eb" },
            }}
          />
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "flex-end",
            gap: 0.75,
            p: 0.75,
            borderRadius: 2.5,
            bgcolor: "#f7f8fa",
            border: "1px solid #e6e8eb",
            "&:focus-within": {
              borderColor: "primary.main",
              bgcolor: "#fff",
              boxShadow: "0 0 0 3px rgba(25,118,210,0.08)",
            },
          }}
        >
          <TextField
            fullWidth
            multiline
            maxRows={4}
            minRows={2}
            size="small"
            placeholder={t("Notas.AddNota", {
              defaultValue: "Write your note…",
            })}
            value={nota}
            onChange={(e) => setNota(e.target.value)}
            variant="standard"
            InputProps={{ disableUnderline: true }}
            sx={{
              px: 0.75,
              py: 0.25,
              "& .MuiInputBase-input": {
                fontSize: "0.9rem",
                lineHeight: 1.45,
              },
            }}
          />
          <Tooltip title="Attach">
            <IconButton
              component="label"
              size="small"
              disabled={saving}
              sx={{ color: "#607d8b", mb: 0.25 }}
            >
              <AttachFileIcon fontSize="small" />
              <input
                type="file"
                hidden
                multiple
                onChange={(e) =>
                  setFiles((prev) => [
                    ...prev,
                    ...Array.from(e.target.files || []),
                  ])
                }
              />
            </IconButton>
          </Tooltip>
          <Tooltip title="Send note">
            <span>
              <IconButton
                color="primary"
                size="small"
                disabled={
                  saving || !canSaveNewNota({ nota, writer, responsible })
                }
                onClick={saveNota}
                sx={{
                  mb: 0.25,
                  bgcolor: "primary.main",
                  color: "#fff",
                  width: 34,
                  height: 34,
                  "&:hover": { bgcolor: "primary.dark" },
                  "&.Mui-disabled": {
                    bgcolor: "#cfd8dc",
                    color: "#fff",
                  },
                }}
              >
                <SendIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </span>
          </Tooltip>
        </Box>

        {files.length > 0 && (
          <Stack
            direction="row"
            spacing={0.5}
            flexWrap="wrap"
            useFlexGap
            sx={{ mt: 1 }}
          >
            {files.map((f, idx) => (
              <Chip
                key={`${f.name}-${idx}`}
                size="small"
                label={f.name}
                onDelete={() =>
                  setFiles((prev) => prev.filter((_, i) => i !== idx))
                }
                deleteIcon={<CloseIcon />}
              />
            ))}
          </Stack>
        )}
      </Box>

      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle
          sx={{ display: "flex", alignItems: "center", gap: 1, color: "#d32f2f" }}
        >
          <WarningAmberIcon /> Delete note
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this note?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={confirmDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
